'use client';

import React, { Suspense, useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSignIn, useAuth } from '@clerk/nextjs';
import type { OAuthStrategy } from '@clerk/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  LogIn,
  AlertCircle,
  CheckCircle2,
  Shield,
  RefreshCw,
} from 'lucide-react';
import { SOCIAL_PROVIDERS, type SupportedProvider } from '@/constants/social-auth';

type Step = 'login' | 'mfa' | 'email-code';

const PROVIDER_STRATEGY: Record<SupportedProvider, OAuthStrategy> = {
  google: 'oauth_google',
  facebook: 'oauth_facebook',
};

const LoginPage = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showVerificationSuccess, setShowVerificationSuccess] = useState(false);
  const [step, setStep] = useState<Step>('login');
  const [totpCode, setTotpCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  // Signed-in users bounce straight to profile builder — arriving here is
  // a no-op otherwise.
  useEffect(() => {
    if (authLoaded && isSignedIn) {
      router.replace('/profile/builder?tab=basic');
    }
  }, [authLoaded, isSignedIn, router]);

  // ?verified=true after email verification / ?error=... from OAuth redirect.
  useEffect(() => {
    if (!searchParams) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (searchParams.get('verified') === 'true') {
      setShowVerificationSuccess(true);
      timer = setTimeout(() => setShowVerificationSuccess(false), 10000);
    }
    const oauthError = searchParams.get('error');
    if (oauthError) {
      setLoginError(oauthError);
    }
    return () => timer && clearTimeout(timer);
  }, [searchParams]);

  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;
    setIsLoading(true);
    setLoginError(null);
    try {
      const result = await signIn.create({
        identifier: formData.email.trim(),
        password: formData.password,
      });
      
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        router.push('/dashboard');
        return;
      }
      
      if (result.status === 'needs_second_factor') {
        // Check what type of second factor is needed
        const secondFactor = result.supportedSecondFactors?.[0];
        
        if (secondFactor?.strategy === 'totp') {
          // TOTP doesn't need a `prepare` step — the secret is already
          // shared with the user's authenticator app at enrollment time.
          setStep('mfa');
          return;
        }
        
        if (secondFactor?.strategy === 'email_code') {
          // Email code needs to be sent first
          console.log('Preparing email code second factor:', secondFactor);
          const prepareResult = await signIn.prepareSecondFactor({
            strategy: 'email_code',
            emailAddressId: secondFactor.emailAddressId,
          });
          console.log('Email code prepared:', prepareResult);
          toast({
            title: 'Verification code sent',
            description: `Check your email at ${formData.email}`,
          });
          setStep('email-code');
          return;
        }
        
        setLoginError('Unsupported second factor method.');
        return;
      }
      
      setLoginError('Unexpected sign-in state. Please retry.');
    } catch (err: any) {
      console.error('Sign-in error:', err);
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Sign-in failed';
      setLoginError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, signIn, setActive, formData.email, formData.password, router, toast]);

  const handleVerifyTotp = useCallback(async () => {
    if (!isLoaded || totpCode.trim().length < 6) return;
    setIsVerifying(true);
    setLoginError(null);
    try {
      const result = await signIn.attemptSecondFactor({
        strategy: 'totp',
        code: totpCode.trim(),
      });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        router.push('/dashboard');
      } else {
        setLoginError('Verification failed. Please try again.');
      }
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Invalid code';
      setLoginError(msg);
    } finally {
      setIsVerifying(false);
    }
  }, [isLoaded, signIn, setActive, totpCode, router]);

  const handleVerifyEmailCode = useCallback(async () => {
    if (!isLoaded || emailCode.trim().length < 6) return;
    setIsVerifying(true);
    setLoginError(null);
    try {
      const result = await signIn.attemptSecondFactor({
        strategy: 'email_code',
        code: emailCode.trim(),
      });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        router.push('/dashboard');
      } else {
        setLoginError('Verification failed. Please try again.');
      }
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Invalid code';
      setLoginError(msg);
    } finally {
      setIsVerifying(false);
    }
  }, [isLoaded, signIn, setActive, emailCode, router]);

  const handleSocialLogin = useCallback(async (provider: SupportedProvider) => {
    if (!isLoaded) return;
    setIsLoading(true);
    try {
      await signIn.authenticateWithRedirect({
        strategy: PROVIDER_STRATEGY[provider],
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/dashboard',
      });
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'OAuth failed';
      toast({ title: 'Social login failed', description: msg, variant: 'destructive' });
      setIsLoading(false);
    }
  }, [isLoaded, signIn, toast]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/assets/brand/Outhere_logo_black.png" alt="OutThere" className="inline-block h-[125px] w-[250px] object-contain dark:hidden" />
          <img src="/assets/brand/Outhere_logo_white.png" alt="OutThere" className="hidden dark:inline-block h-[125px] w-[250px] object-contain" />
          <p className="text-muted-foreground mt-2">Sign in to your creator account</p>
        </div>

        <Card>
          {step === 'email-code' ? (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email verification
                </CardTitle>
                <CardDescription>
                  Enter the 6-digit code sent to {formData.email}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-code">Verification code</Label>
                  <Input
                    id="email-code"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                  />
                </div>
                {loginError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-md flex items-start text-sm">
                    <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{loginError}</span>
                  </div>
                )}
                <Button onClick={handleVerifyEmailCode} disabled={isVerifying || emailCode.length < 6} className="w-full">
                  {isVerifying ? (
                    <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Verifying…</>
                  ) : (
                    <><LogIn className="mr-2 h-4 w-4" />Verify & continue</>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep('login'); setEmailCode(''); setLoginError(null); }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  Back to sign in
                </button>
              </CardContent>
            </>
          ) : step === 'mfa' ? (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Two-factor authentication
                </CardTitle>
                <CardDescription>
                  Enter the 6-digit code from your authenticator app.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="totp">Authenticator code</Label>
                  <Input
                    id="totp"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                  />
                </div>
                {loginError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-md flex items-start text-sm">
                    <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{loginError}</span>
                  </div>
                )}
                <Button onClick={handleVerifyTotp} disabled={isVerifying || totpCode.length < 6} className="w-full">
                  {isVerifying ? (
                    <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Verifying…</>
                  ) : (
                    <><LogIn className="mr-2 h-4 w-4" />Verify & continue</>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep('login'); setTotpCode(''); setLoginError(null); }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  Back to sign in
                </button>
              </CardContent>
            </>
          ) : (
            <Tabs defaultValue="email">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="email">Email</TabsTrigger>
                <TabsTrigger value="social">Social</TabsTrigger>
              </TabsList>
              <TabsContent value="email">
                <CardHeader>
                  <CardTitle>Login with email</CardTitle>
                  <CardDescription>Enter your email and password</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    {showVerificationSuccess && (
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <AlertTitle className="text-green-800">Email verified</AlertTitle>
                        <AlertDescription className="text-green-700">
                          Your email has been verified. Sign in to continue.
                        </AlertDescription>
                      </Alert>
                    )}
                    {loginError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{loginError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => { setFormData((p) => ({ ...p, email: e.target.value })); if (loginError) setLoginError(null); }}
                          placeholder="you@example.com"
                          autoComplete="email"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <Link href="/auth/reset-password" className="text-xs text-blue-600 hover:text-blue-800">
                          Forgot password?
                        </Link>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => { setFormData((p) => ({ ...p, password: e.target.value })); if (loginError) setLoginError(null); }}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          className="pl-10"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1 h-8 w-8 p-0"
                          onClick={() => setShowPassword((s) => !s)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          <span className="sr-only">{showPassword ? 'Hide password' : 'Show password'}</span>
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      By signing in, you agree to our{' '}
                      <Link href="/terms" className="text-blue-600 hover:text-blue-800">Terms</Link> and{' '}
                      <Link href="/privacy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>.
                    </p>
                    <Button type="submit" disabled={isLoading || !isLoaded} className="w-full bg-red-500 hover:bg-red-600">
                      {isLoading ? (
                        <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Signing in…</>
                      ) : (
                        <><LogIn className="mr-2 h-4 w-4" />Sign in</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </TabsContent>
              <TabsContent value="social">
                <CardHeader>
                  <CardTitle>Login with social</CardTitle>
                  <CardDescription>Use Google or Facebook</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {SOCIAL_PROVIDERS.map(({ provider, label, icon: Icon }) => (
                    <Button
                      key={provider}
                      variant="outline"
                      className="relative w-full flex items-center justify-center px-8"
                      onClick={() => handleSocialLogin(provider)}
                      disabled={isLoading || !isLoaded}
                    >
                      <Icon className="absolute left-6 top-1/2 -translate-y-1/2" />
                      <span className="w-full text-center leading-5">{label}</span>
                    </Button>
                  ))}
                </CardContent>
              </TabsContent>
            </Tabs>
          )}

          {step === 'login' && (
            <CardFooter className="flex flex-col items-center justify-center space-y-2 pt-0">
              <div className="text-sm text-muted-foreground">
                Don&apos;t have an account?{' '}
                <Link href="/auth/signup" className="text-blue-600 hover:text-blue-800 font-medium">
                  Sign up for free
                </Link>
              </div>
            </CardFooter>
          )}
        </Card>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} OutThere. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default function Page() {
  return (
    <Suspense fallback={null}>
      <LoginPage />
    </Suspense>
  );
}
